export const WORKSPACE_IDS = [
  'SERVICE',
  'KITCHEN',
  'ANALYTICS',
  'INVENTORY',
  'HR',
  'POS',
  'CUSTOMER_HISTORY',
  'MARKETING',
  'MENU',
  'LOYALTY',
] as const;

export type WorkspaceId = (typeof WORKSPACE_IDS)[number];

/** Constructor is a manager-only destination, not an access-policy workspace. */
export type PanelDestinationId = WorkspaceId | 'CONSTRUCTOR';

export type PanelNavigationRole = 'sala' | 'cocina' | 'gerencia';

type WorkspaceGrantProvenance = 'published_membership' | 'derived_assignment' | 'manager_configuration';

interface PanelWorkspaceGrant {
  readonly workspaceId: WorkspaceId;
  readonly provenance: WorkspaceGrantProvenance;
}

/*
 * Internal presentation model, not a wire contract. The later generated
 * Identity adapter must validate and map the canonical response before it can
 * satisfy the runtime port with this value.
 */
export interface PanelAccessModel {
  readonly restaurantId: string;
  readonly subjectUserId: string;
  readonly grants: readonly PanelWorkspaceGrant[];
  readonly defaultRoute: string | null;
  readonly exactPermissions: readonly string[];
  readonly navigationEnabled: boolean;
  /**
   * Presentation profile supplied by the future canonical Identity adapter.
   * It only narrows already-granted destinations; it never grants access.
   */
  readonly navigationRole: PanelNavigationRole;
  /**
   * Derived by the Identity adapter from exact restaurant-manager authority.
   * Constructor is intentionally not one of the ten grantable workspaces.
   */
  readonly constructorEnabled?: boolean;
}

export interface PanelDestination {
  readonly workspaceId: PanelDestinationId;
  readonly label: string;
  readonly shortLabel: string;
  readonly path: string;
  readonly group: PanelDestinationGroup;
}

export const PANEL_DESTINATION_GROUPS = ['Gestión', 'Departamentos', 'Operativas'] as const;

type PanelDestinationGroup = (typeof PANEL_DESTINATION_GROUPS)[number];

/* Frozen P0 live overlay. POS stays distinct from SERVICE. */
export const PANEL_DESTINATIONS: readonly PanelDestination[] = Object.freeze([
  { workspaceId: 'CONSTRUCTOR', label: 'Constructor', shortLabel: 'Plano', path: '/app/constructor', group: 'Gestión' },
  { workspaceId: 'ANALYTICS', label: 'Analítica', shortLabel: 'Analítica', path: '/app/analytics', group: 'Departamentos' },
  { workspaceId: 'MARKETING', label: 'Marketing', shortLabel: 'Marketing', path: '/app/promotions', group: 'Departamentos' },
  { workspaceId: 'LOYALTY', label: 'Tarjetas', shortLabel: 'Tarjetas', path: '/app/cards', group: 'Departamentos' },
  { workspaceId: 'INVENTORY', label: 'Inventario', shortLabel: 'Inventario', path: '/app/inventory', group: 'Departamentos' },
  { workspaceId: 'CUSTOMER_HISTORY', label: 'Historial de pedidos', shortLabel: 'Historial', path: '/app/order-history', group: 'Departamentos' },
  { workspaceId: 'HR', label: 'Recursos Humanos', shortLabel: 'RRHH', path: '/app/employees', group: 'Departamentos' },
  { workspaceId: 'MENU', label: 'Menú', shortLabel: 'Menú', path: '/app/menu', group: 'Departamentos' },
  { workspaceId: 'SERVICE', label: 'Servicio', shortLabel: 'Servicio', path: '/app/waiterdelivers', group: 'Operativas' },
  { workspaceId: 'KITCHEN', label: 'Cocina', shortLabel: 'Cocina', path: '/app/dishes', group: 'Operativas' },
  { workspaceId: 'POS', label: 'TPV', shortLabel: 'TPV', path: '/app/orders', group: 'Operativas' },
]);

/*
 * Each role's own operational destinations, from v20's `ROL_SCOPE`
 * (docs/design/panel-v20/census/_logic.js line 294):
 *   Sala   → ['waiter', 'tpv']  = Servicio, TPV
 *   Cocina → ['kitchen']        = Cocina
 * They lead a restricted shell's listing, which since 4 November 2026 is every
 * granted destination (`navigationScope`); the gerencia set is the manager's
 * compact rail. The bottom bar is not this set: see `bottomBarDestinations`.
 */
const ROLE_DESTINATION_IDS = {
  sala: ['SERVICE', 'POS'],
  cocina: ['KITCHEN'],
  // Home joins this set in P14, when that route exists. P1 must not invent it.
  gerencia: ['ANALYTICS', 'CUSTOMER_HISTORY', 'HR'],
} as const satisfies Readonly<Record<PanelNavigationRole, readonly WorkspaceId[]>>;

const NAVIGATION_ROLE_LABELS = {
  sala: 'Sala',
  cocina: 'Cocina',
  gerencia: 'Gerencia',
} as const satisfies Readonly<Record<PanelNavigationRole, 'Sala' | 'Cocina' | 'Gerencia'>>;

export const navigationRoleLabel = (role: PanelNavigationRole): 'Sala' | 'Cocina' | 'Gerencia' => NAVIGATION_ROLE_LABELS[role];

/**
 * Uses the extract's role-specific compact sets, intersected with canonical
 * grants.
 */
export const roleDestinations = (
  destinations: readonly PanelDestination[],
  role: PanelNavigationRole,
): readonly PanelDestination[] => {
  const grantedByWorkspace = new Map(destinations.map((destination) => [destination.workspaceId, destination] as const));
  return ROLE_DESTINATION_IDS[role].flatMap((workspaceId) => {
    const destination = grantedByWorkspace.get(workspaceId);
    return destination === undefined ? [] : [destination];
  });
};

/**
 * What a shell *lists*: every granted destination. A non-manager's grants are
 * their placements, compiled by the backend (Cocina for any kitchen-kind
 * department, Servicio for the floor, TPV, and any access department such as
 * Inventario or Menú), so the navigation is the placement and nothing else
 * (docs/decisions/2026-11-04-staff-navigation-from-placement.md, which amends
 * D-160 ruling 20). A restricted role orders its own operational destinations
 * first (`roleDestinations`), so its Board opens them; the rest follow in the
 * Panel's order. Management is listed every granted destination, as v20 lists
 * it.
 *
 * This is presentation only: the grants, `isWorkspaceGranted`, the admission
 * checks in `src/app/**` and the deny-and-return route read
 * `visibleDestinations`.
 */
export const navigationScope = (
  destinations: readonly PanelDestination[],
  role: PanelNavigationRole,
): readonly PanelDestination[] => {
  if (role === 'gerencia') return destinations;
  const first = roleDestinations(destinations, role);
  return [...first, ...destinations.filter((destination) => !first.includes(destination))];
};

/*
 * The bottom bar of a touch device, as the staff drawings list it
 * (docs/design/panel-v20/mockups/staff-v20/v20.js, `[data-bottomnav]`):
 * TPV, Servicio, Cocina, in that order, for every role (owner ruling of
 * 11 October 2026). Every other destination is under «Más».
 */
const BOTTOM_BAR_DESTINATION_IDS = ['POS', 'SERVICE', 'KITCHEN'] as const satisfies readonly WorkspaceId[];

/**
 * The service destinations a shell lists in its bottom bar: the three of the
 * drawing, cut to what is listed (`navigationScope`, the placements), so a
 * waiter sees TPV and Servicio, a cook Cocina, a person on the floor and in a
 * kitchen all three, and a manager all three. Presentation
 * only: a destination that is not granted is never in `destinations`, and
 * admission stays with the route registry.
 */
export const bottomBarDestinations = (
  destinations: readonly PanelDestination[],
  role: PanelNavigationRole,
): readonly PanelDestination[] => {
  const listed = new Map(navigationScope(destinations, role).map((destination) => [destination.workspaceId, destination] as const));
  return BOTTOM_BAR_DESTINATION_IDS.flatMap((workspaceId) => {
    const destination = listed.get(workspaceId);
    return destination === undefined ? [] : [destination];
  });
};

export const visibleDestinations = (access: PanelAccessModel): readonly PanelDestination[] => {
  if (!access.navigationEnabled) return [];
  const allowed = new Set(access.grants.map((grant) => grant.workspaceId));
  return PANEL_DESTINATIONS.filter((destination) =>
    destination.workspaceId === 'CONSTRUCTOR'
      ? access.constructorEnabled === true
      : allowed.has(destination.workspaceId),
  );
};

export const isWorkspaceGranted = (access: PanelAccessModel, workspaceId: PanelDestinationId): boolean =>
  visibleDestinations(access).some((destination) => destination.workspaceId === workspaceId);

/** The server's own reading of the membership's canonical roles: this person runs the restaurant. */
export const runsTheRestaurant = (access: PanelAccessModel): boolean => access.navigationRole === 'gerencia';

export const hasExactPermission = (access: PanelAccessModel, permission: string): boolean =>
  access.exactPermissions.includes(permission);
